import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import VirtualizedListTypeCarousel from '@/components/feature/list-type/VirtualizedListTypeCarousel';
import { CustomMenuListCode } from '@/constants/custom-menu-list-code';
import { useCurrentCustomMenu } from '@/hooks/custom-menu';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores';
import type { ListTypeListItem } from '@/types/list-type';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import styles from './ListTypePage.module.scss';
import { useTranslation } from 'react-i18next';
import HotplaceListTypePage from './hotplace/HotplaceListTypePage';
import EventPage from '../event/eventPage';

const ListTypePage = () => {
    const { t } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const { isHorizontal } = useOrientation();
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();
    const navigate = useNavigate();

    const { currentMenu, customContent, menuCode } = useCurrentCustomMenu(gnbMenuId);

    const pageClassname = useMemo(() => {
        return clsx(styles['list-type'], {
            [styles['list-type__vertical']]: !isHorizontal,
            [styles['list-type__horizontal']]: isHorizontal,
            [styles['list-type__contrast']]: ui.contrast,
            [styles['list-type__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['list-type__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, ui.contrast, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['list-type__carousel'], {
            [styles['list-type__carousel__horizontal']]: isHorizontal,
            [styles['list-type__carousel__vertical']]: !isHorizontal,
            [styles['list-type__carousel__contrast']]: ui.contrast,
            [styles['list-type__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['list-type__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['list-type__carousel__vertical__empty']]: customContent.length === 0 && !isHorizontal,
            [styles['list-type__carousel__vertical__empty__halfmode']]: customContent.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['list-type__carousel__horizontal__empty']]: customContent.length === 0 && isHorizontal,
            [styles['list-type__carousel__horizontal__empty__halfmode']]: customContent.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, ui.contrast, customContent.length]);

    switch (menuCode) {
        case CustomMenuListCode.HOTPLACE:
            return <HotplaceListTypePage gnbMenuId={gnbMenuId} />;

        case CustomMenuListCode.EVENT:
            return <EventPage />;
        default:
            break;
    }

    const handleItemClick = (item: ListTypeListItem) => {
        const menuId = gnbMenuId ?? currentMenu?.id ?? '';
        if (!menuId) return;
        navigate(PATHS.CUSTOM_MENU.LIST_TYPE_DETAIL(menuId, item.id));
    };

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['list-type__header'], {
                    [styles['list-type__header--horizontal']]: isHorizontal,
                    [styles['list-type__header--vertical']]: !isHorizontal,
                    [styles['list-type__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader
                    title={(currentMenu?.name as string) || ''}
                    description={currentMenu?.description || ''}
                    isHorizontal={isHorizontal}
                    tabIndex={1}
                />
            </div>

            <div className={carouselClassname}>
                {customContent.length > 0 ? (
                    <VirtualizedListTypeCarousel contents={customContent} onClickItem={handleItemClick} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="list-type-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    />
                )}
            </div>
        </div>
    );
};

export default ListTypePage;
